import React from 'react';
import { useMutation } from 'react-apollo';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Theme,
  Typography,
} from '@material-ui/core';
import pluralize from 'pluralize';
import { useModal } from 'src/app-builder';

import { useNotification } from 'src/hooks';
import { SubmitButton } from 'src/components';
import { LOAN_APPLICATIONS_DELETE_MUTATION } from 'src/graphql';
import { DIALOGS } from './constants';
import { isFunction, t } from 'src/utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: theme.spacing(60),
  },
}));

interface LoanApplicationDeleteDialogProps {}

export const LoanApplicationDeleteDialog: React.FC<LoanApplicationDeleteDialogProps> = () => {
  const classes = useStyles();

  const notification = useNotification();

  const { open, args, closeModal } = useModal(DIALOGS.LOAN_APPLICATION_DELETE_DIALOG);

  const loanApplicationsCount = Array.isArray(args?.id) ? args?.id?.length : 1;
  const formattedLoanApplications = pluralize('Loan Applications', loanApplicationsCount);

  const [deleteRow, { loading }] = useMutation(LOAN_APPLICATIONS_DELETE_MUTATION, {
    onError: () =>
      notification.error(
        t('loan_application_delete_error', {
          formattedLoanApplications,
        }),
      ),
  });

  const onDelete = React.useCallback(async () => {
    if (Array.isArray(args?.id)) {
      await Promise.all(
        args?.id.slice(1).map((id: string) =>
          deleteRow({
            variables: { filter: { id }, force: true },
          }),
        ),
      );

      await deleteRow({
        variables: { filter: { id: args?.id[0] }, force: true },
        refetchQueries: ['SortableLoanApplicationsList', 'LoanApplicationsMetrics'],
        awaitRefetchQueries: true,
      });
    } else {
      await deleteRow({
        variables: { filter: { id: args?.id }, force: true },
        refetchQueries: ['SortableLoanApplicationsList', 'LoanApplicationsMetrics'],
        awaitRefetchQueries: true,
      });
    }

    notification.success(
      t('loan_application_delete_success', {
        formattedLoanApplications,
      }),
    );

    if (isFunction(args?.onSuccess)) {
      args.onSuccess();
    }

    closeModal();
  }, [closeModal, args, deleteRow, notification, formattedLoanApplications]);

  const onClose = React.useCallback(() => {
    closeModal();
  }, [closeModal]);

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" PaperProps={{ className: classes.root }}>
      <DialogTitle disableTypography>
        <Typography variant="h6">Delete {formattedLoanApplications}</Typography>
      </DialogTitle>

      <DialogContent>
        <DialogContentText>
          Are you sure want to delete this {formattedLoanApplications.toLowerCase()}?
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <SubmitButton
          color="secondary"
          variant="contained"
          type="submit"
          onClick={onDelete}
          loading={loading}
        >
          Delete {formattedLoanApplications}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
};
